import { CouchMatch } from '../sim/couch';
import { DRIVERS, driverById } from '../sim/drivers';
import { RaceWorld, RacerSpec } from '../sim/race';
import { mulberry32 } from '../sim/rng';
import { TEAMS, TeamSpec, teamById } from '../sim/teams';
import { trackById } from '../sim/daily';
import type { OnlineSettings, Roster } from './protocol';

/**
 * Builds the same race on every device from the start message (design.md
 * B8.2): who drives which car, the paint jobs, the grid. Pure and
 * deterministic: any difference here would desync the race.
 */

/** Player liveries in roster order: red, blue, yellow, green. */
export const SLOT_LIVERIES = ['ferro', 'wolfe', 'lotos', 'bennet'];
export const SLOT_COLORS = [0xff5050, 0x5a8cff, 0xffd21a, 0x3fd25a];
const CPU_DRIVERS = ['josh', 'mick', 'tina', 'john', 'jody'];
/** Online AI drives at "pro" pace (design.md B7). */
export const ONLINE_AI_PACE = 1.28;

export interface Lineup {
  specs: RacerSpec[];
  /** Racer indices driven by people. */
  humanSlots: number[];
  /** Grid order (race mode): grid[pos] = racer index. */
  grid: number[];
}

export function lineup(settings: OnlineSettings, roster: Roster[], seed: number): Lineup {
  const perf = teamById(settings.car);
  const used = new Set<string>();
  const paint = (liveryId: string): TeamSpec => {
    used.add(liveryId);
    const l = teamById(liveryId);
    return { ...l, zeroTo100: perf.zeroTo100, topSpeedMph: perf.topSpeedMph };
  };
  const humans = [...roster].sort((a, b) => a.slot - b.slot);
  const specs: RacerSpec[] = humans.map((r, i) => {
    const tag = r.name || `P${i + 1}`;
    return { id: `p${r.player}`, name: tag, tag, team: paint(SLOT_LIVERIES[i]) };
  });
  const maxCars = settings.mode === 'h2h' ? 4 : 6;
  const cpus = Math.max(0, Math.min(settings.cpus, maxCars - specs.length));
  const spare = [...TEAMS.map((t) => t.id).filter((id) => !SLOT_LIVERIES.includes(id)), ...SLOT_LIVERIES];
  for (let k = 0; k < cpus; k++) {
    const d = driverById(CPU_DRIVERS[k]) ?? DRIVERS[k];
    const livery = spare.find((id) => !used.has(id)) ?? 'marlin';
    specs.push({ id: d.id, name: d.name, tag: d.tag, team: paint(livery), driver: d });
  }
  const grid = specs.map((_, i) => i);
  if (settings.mode === 'race') {
    const rnd = mulberry32(seed ^ 0x6a1d);
    for (let i = grid.length - 1; i > 0; i--) {
      const j = Math.floor(rnd() * (i + 1));
      [grid[i], grid[j]] = [grid[j], grid[i]];
    }
  }
  return { specs, humanSlots: humans.map((_, i) => i), grid };
}

export function buildRace(settings: OnlineSettings, l: Lineup, seed: number): RaceWorld {
  return new RaceWorld(trackById(settings.track), l.specs, l.grid, { laps: settings.laps, crashRule: 'respawn', aiPace: ONLINE_AI_PACE, seed });
}

export function buildMatch(settings: OnlineSettings, l: Lineup, seed: number): CouchMatch {
  return new CouchMatch(trackById(settings.track), l.specs, { lights: settings.lights, aiPace: ONLINE_AI_PACE, seed });
}
